<script lang="ts" setup>
import { computed, ref, watch, PropType } from 'vue';
import { required, email, helpers, numeric } from "@vuelidate/validators";
import { useVuelidate, ValidationRule, ValidationRuleWithoutParams, ValidationRuleWithParams } from "@vuelidate/core";
 
import { DensityType } from "@/app/common/types/form.type";
import { useI18n } from "vue-i18n";

const props = defineProps({
  modelValue: { default: {value:"",isValid:true} },
  title: { type: String, default: "" },
  isRequired: { type: Boolean, default: false },
  isEmail: { type: Boolean, default: false },
  isNumber: { type: Boolean, default: false },
  isIdCard:{ type: Boolean, default: false },
  isPassport:{ type: Boolean, default: false },
  externalError:{type:String,default:undefined},
  placeholder: { type: String, default: "" },
  hideDetails: { type: Boolean, default: false },
  showError: { type: Boolean, default: false },
  type: { type: String, default: "text" },
  isSubmitted: { type: Boolean, default: false },
  maxLength: { type: String, default: undefined },
  id: { type: String, default: undefined },
  isPassword: { type: Boolean, default: false },
  isConfirmPassword : { type: Boolean, default: false },
  confirmPassword: { type: String, default: undefined },
  disabled: { type: Boolean, default: false },
  class: { type: String, default: "" },
  label: { type: String, default: "" },
  density: {
    type: String as PropType<DensityType>,
    default: "compact"
  }
});
const emit = defineEmits(["update:modelValue", "keyup"]);

const showPassword = ref(false);
const {t} = useI18n()
const fieldValue = ref(props.modelValue.value)

const externalErrorMessage = computed(()=> props.externalError || "")
const hasExternalError = computed(()=> props.externalError !== undefined && props.externalError !== null && props.externalError !== "")

const isValidation = computed(() => {
  return props.isRequired ||props.isNumber || props.isEmail || props.isPassword  || props.isConfirmPassword || props.isIdCard || props.isPassport || hasExternalError.value;
});

watch(fieldValue,async (newValue )=>{
  if (isValidation.value) {
      emit("update:modelValue", {
        value: newValue,
        isValid: isValid.value
      });
    } else {
      emit("update:modelValue", newValue);
    }
})
const idCardFormat = (id:string) => {
    if(!id) return false
    if (id.length != 13) return false;
    // STEP 1 - get only first 12 digits
    let i,sum;
    for ( i = 0, sum = 0; i < 12; i++) {
      // STEP 2 - multiply each digit with each index (reverse)
      // STEP 3 - sum multiply value together
      sum += parseInt(id.charAt(i)) * (13 - i);
    }
    // STEP 4 - mod sum with 11
    let mod = sum % 11;
    // STEP 5 - subtract 11 with mod, then mod 10 to get unit
    let check = (11 - mod) % 10;
    // STEP 6 - if check is match the digit 13th is correct
    if (check == parseInt(id.charAt(12))) {
      return true;
    }
    return false;
  }
const passportFormat = (value:string) =>!!value  &&  value != ""  
const passwordFormat = (value:string) => !!value  &&  value != "" &&  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[\d@#$!%*?&])[A-Za-z\d@#$!%*?&]{8,}$/.test(value)
const comformPassword = (value:string) => props.confirmPassword == value
const externalErrorCheck = (value:string) => props.externalError ==""
const rules = computed(() => ({
  fieldValue: {
    required: props.isRequired ? helpers.withMessage('t-please-fill' ,required) : {},
    number:props.isNumber?helpers.withMessage('t-invalid-number' ,numeric) : {},
    email: props.isEmail ? helpers.withMessage('t-invalid-email' ,email) : {},
    password: props.isPassword ?helpers.withMessage('t-invalid-password', passwordFormat): {},
    confirmPassword: props.isConfirmPassword ?helpers.withMessage('t-error-confirm-password-not-match', comformPassword): {},
    idCard:props.isIdCard  ?helpers.withMessage('t-invalid-id-card', idCardFormat): {},
    passport:props.isPassport  ?helpers.withMessage('t-invalid-passport', passportFormat): {},
    externalError:hasExternalError.value  ?helpers.withMessage(externalErrorMessage.value,externalErrorCheck): {},
  }
}));

const v$ = useVuelidate(rules, { fieldValue });

const onChange = () => {
  if (isValidation.value) {
    v$.value.$touch();
    emit("update:modelValue", {
      value: fieldValue.value,
      isValid: isValid.value
    });
  }
};

const isSubmitted = computed(() => props.isSubmitted);
const isValid = computed(() => v$.value.fieldValue.$errors.length === 0 && (props.externalError == "" || props.externalError === undefined));
const getType = computed(() => {
  if (props.isPassword || props.confirmPassword != undefined) {
    if (!showPassword.value) {
      return "password";
    }
  }

  return props.type || "text";
});

watch(isSubmitted, () => {
  onChange();
});

watch(() => props.externalError, () => {
  if (isValidation.value) {
    v$.value.$touch();
  }
});
</script>
<template>
  <div :class="props.class">
    <v-text-field
      v-model="fieldValue"
      variant="solo"
      :density="density"
      :type="getType"
      class="text-field-component"
      :hide-details="hideDetails"
      :placeholder="placeholder"
      :maxLength="maxLength"
      :label="label"
      :disabled="disabled"
      :id="id"
      :class="{
        'is-invalid': isSubmitted && showError && !isValid,
        'is-valid': isSubmitted && showError && isValid
      }"
      @input="onChange"
      @keyup="emit('keyup', $event)"
    >
      <template #append-inner>
        <v-btn
          v-if="isPassword || confirmPassword != undefined"
          density="compact"
          class="text-muted"
          :icon="showPassword ? 'ph-eye' : 'ph-eye-slash'"
          @click="showPassword = !showPassword"
        />
        <slot name="append-inner" />
      </template>
      <template #prepend-inner>
        <slot name="prepend-inner" />
      </template>
    </v-text-field>
  </div>
  <div
    v-if="isSubmitted && showError && v$.fieldValue.$invalid"
    class="invalid-feedback"
  >
    <span v-for="(error, index) in v$.fieldValue.$errors" :key="index">
      {{ $t(error.$message.toString()) }}<template v-if="index < v$.fieldValue.$errors.length - 1">, </template>
    </span>
  </div>
  <div v-if="isSubmitted && showError && !v$.fieldValue.$invalid">
    <slot name="success-message" />
  </div>
</template>
